import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  signal,
  inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  AbstractControl,
  ValidationErrors,
  Validator,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';

@Component({
  selector: 'app-input',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
})
export class InputComponent implements ControlValueAccessor, Validator {

  // ── Inputs ───────────────────────────────────────────────────────────────────
  @Input() placeholder = '';
  @Input() type: 'text' | 'email' | 'password' | 'number' | 'search' = 'text';
  @Input() required = false;
  @Input() disabled = false;
  @Input() errorMessage = 'Ce champ est obligatoire';
  @Input() clearable = false;
  @Input() suffix    = '';
  /** Texte grisé affiché avant la saisie (« < », « -1 to »…). */
  @Input() prefix    = '';
  /** Texte grisé collé juste après la valeur saisie (« to 1 »), l'unité `suffix` restant calée à droite. */
  @Input() inlineSuffix = '';
  @Input() maxlength: number | null = null;
  /** Avec `type="number"` : n'accepte que des entiers et garde le champ natif (flèches, molette). */
  @Input() integer = false;
  /** Nom accessible du champ natif, quand son libellé est dans un autre élément (légende voisine, titre de section…). */
  @Input() ariaLabel = '';

  @Output() cleared = new EventEmitter<void>(); // émis quand l'utilisateur clique sur ×

  // ── State ────────────────────────────────────────────────────────────────────
  value     = signal('');
  isFocused = signal(false);
  isTouched = signal(false);

  private readonly _cdr = inject(ChangeDetectorRef);

  // ── CVA ──────────────────────────────────────────────────────────────────────
  private _onChange:  (v: string) => void = () => {};
  private _onTouched: () => void          = () => {};

  writeValue(val: string): void                    { this.value.set(val == null ? '' : String(val)); }
  registerOnChange(fn: (v: string) => void): void  { this._onChange = fn; }
  registerOnTouched(fn: () => void): void          { this._onTouched = fn; }

  /**
   * `disabled` n'est pas un signal : sans `markForCheck`, un `enable()`/`disable()`
   * posé sur le contrôle après le premier rendu ne repeindrait pas le champ.
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this._cdr.markForCheck();
  }

  // ── Validator ─────────────────────────────────────────────────────────────────
  validate(_: AbstractControl): ValidationErrors | null {
    return this.required && !this.value() ? { required: true } : null;
  }

  // ── Events ────────────────────────────────────────────────────────────────────
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const val   = input.value;

    if (this.type === 'number' && this.integer) {
      // Saisie transitoire invalide (« - » seul…) : `input.value` vaut `''`, on ne touche pas au
      // signal pour que le binding `[value]` n'efface pas le champ.
      if (input.validity.badInput) return;
      this.value.set(val);
      this._onChange(val);
      return;
    }

    if (this.type === 'number') {
      // Accepte « , » comme « . » quelle que soit la locale ; la valeur émise est toujours normalisée.
      const normalized = val.replace(',', '.');
      if (!/^-?\d*\.?\d*$/.test(normalized)) {
        input.value = this.value(); // caractère refusé : on restaure la saisie précédente
        return;
      }
      this.value.set(val);
      this._onChange(normalized);
      return;
    }

    this.value.set(val);
    this._onChange(val);
  }

  /** Le champ natif accepte « . », « , », « e » et « + » : on les bloque en mode entier. */
  onKeydown(event: KeyboardEvent): void {
    if (this.type === 'number' && this.integer && ['.', ',', 'e', 'E', '+'].includes(event.key)) {
      event.preventDefault();
    }
  }

  onFocus(): void { this.isFocused.set(true); }

  onBlur(): void {
    this.isFocused.set(false);
    this.isTouched.set(true);
    this._onTouched();
  }

  onClear(): void {
    this.value.set('');
    this._onChange('');
    this._onTouched();
    this.cleared.emit();
  }

  // ── Helpers ───────────────────────────────────────────────────────────────────
  /**
   * Un `<input type="number">` natif suit la locale du navigateur : en français, « 1. » est une
   * saisie invalide, `input.value` vaut alors `''` et le binding `[value]` effaçait le champ.
   * On rend donc un champ texte avec clavier décimal et on valide nous-mêmes.
   */
  get nativeType(): string { return this.type === 'number' && !this.integer ? 'text' : this.type; }

  get showError(): boolean { return this.required && this.isTouched() && !this.value(); }

  /** Avec `inlineSuffix`, le champ prend la largeur de son contenu pour que le texte le suive. */
  get contentSize(): number | null {
    if (!this.inlineSuffix) return null;
    return Math.max(this.value().length, this.placeholder.length, 1);
  }
}